import { useMemo } from 'react'
import dayjs from 'dayjs'
import { Datepicker, Icon, Text } from '@/components'

export interface DateRangePickerProps {
  /** ISO date string (YYYY-MM-DD) or null when unset */
  startDate: string | null
  /** ISO date string (YYYY-MM-DD) or null when unset */
  endDate: string | null
  onChange: (startDate: string | null, endDate: string | null) => void
  disabled?: boolean
  id?: string
}

type RangeInputProps = {
  label: string
  placeholder: string
  value: string | null
  disabled?: boolean
  error?: boolean
}

function RangeInput({
  label,
  placeholder,
  value,
  disabled,
  error,
}: RangeInputProps) {
  const borderClass = error ? 'border-themed-red-600' : 'border-themed-grey-200'
  const backgroundClass = disabled ? 'bg-themed-grey-100' : 'bg-white'
  const labelClass = disabled ? 'text-themed-grey-400' : 'text-themed-grey-500'
  const valueClass =
    disabled || !value ? 'text-themed-grey-400' : 'text-themed-grey-500'

  return (
    <div
      className={`relative flex-1 box-border border ${borderClass} ${backgroundClass} rounded-lg p-2 min-w-0 ${
        disabled ? '' : 'cursor-pointer'
      }`}
    >
      <Text variant="body1" className={`!text-[14px] ${labelClass}`}>
        {label}
      </Text>
      <Text variant="body2" className={`truncate pr-5 ${valueClass}`}>
        {value ? dayjs(value).format('MMM D, YYYY') : placeholder}
      </Text>
      <div className="absolute top-2 right-2">
        <Icon
          name="Calendar"
          size={16}
          color={disabled ? 'grey-400' : 'grey-500'}
        />
      </div>
    </div>
  )
}

/**
 * Design System v2 Date Range Picker: paired start/end date inputs, each
 * opening a calendar, with range validation. Composed from the existing
 * component-library Datepicker.
 */
export default function DateRangePicker({
  startDate,
  endDate,
  onChange,
  disabled,
  id = 'date-range-picker',
}: DateRangePickerProps) {
  const hasInvalidRange = useMemo(
    () =>
      startDate !== null &&
      endDate !== null &&
      dayjs(endDate).isBefore(dayjs(startDate), 'day'),
    [startDate, endDate],
  )

  return (
    <div className="w-full">
      <div className="flex items-center gap-2 w-full">
        {/* Relative wrappers anchor the library Datepicker's absolutely-positioned
            popover to each input; without one it positions against the page. */}
        <div className="relative flex-1 min-w-0">
          <Datepicker
            id={`${id}-start`}
            closeOnChange
            closeOnOutsideClick
            elevated
            initialValue={startDate}
            onChange={(date) => onChange(date, endDate)}
            resetOnOpen={startDate === null}
            style={{
              transformOrigin: 'top left',
              left: 0,
              boxSizing: 'border-box',
            }}
          >
            <Datepicker.Activator>
              <RangeInput
                label="Start date"
                placeholder="Select start date"
                value={startDate}
                disabled={disabled}
                error={hasInvalidRange}
              />
            </Datepicker.Activator>
          </Datepicker>
        </div>
        <Text variant="body2" className="text-themed-grey-400 shrink-0">
          →
        </Text>
        <div className="relative flex-1 min-w-0">
          <Datepicker
            id={`${id}-end`}
            closeOnChange
            closeOnOutsideClick
            elevated
            initialValue={endDate}
            onChange={(date) => onChange(startDate, date)}
            resetOnOpen={endDate === null}
            style={{
              transformOrigin: 'top right',
              right: 0,
              boxSizing: 'border-box',
            }}
          >
            <Datepicker.Activator>
              <RangeInput
                label="End date"
                placeholder="Select end date"
                value={endDate}
                disabled={disabled}
                error={hasInvalidRange}
              />
            </Datepicker.Activator>
          </Datepicker>
        </div>
      </div>
      {hasInvalidRange && (
        <Text variant="body4" className="!text-themed-red-600 mt-1">
          Please select a valid date range
        </Text>
      )}
    </div>
  )
}
